/**
 * ================================================================================================
 *  THE PAGE THE VERIFICATION LINK LANDS ON.
 * ================================================================================================
 *
 * The Owner, 2026-10-29, section 1: *"שינוי מייל כניסה יכלול אימות הכתובת החדשה."*
 *
 * ── WHY THIS SCREEN HAD TO EXIST BEFORE THE MAIL COULD BE SENT ─────────────────────────────────
 *
 * `MAIL_LINK_PATHS.email_verification` was `MAIL_LINK_NOT_CONFIGURED`, and the comment beside it
 * said exactly why: *"no route in `apps/web` serves an e-mail-verification redemption and NO
 * PRODUCER in this product sends that kind. A plausible-looking `/verify-email` here would be an
 * address nothing serves, offered to the next caller as though it worked."* `buildLink` REFUSES a
 * kind with no served route, by name — so the first `email_verification` message this product
 * tried to send would have thrown, and the feature would have failed at the one step nobody can
 * test without a mailbox.
 *
 * That control did its job: the producer came first, and it was refused until the page existed.
 *
 * ── IT IS PRE-SESSION, AND IT ACTS ON ARRIVAL ──────────────────────────────────────────────────
 *
 * The link is followed from a mailbox. The person may not be signed in, or may be signed in as a
 * different account in that browser, so nothing here reads a session and nothing here needs one:
 * the TOKEN is the whole authority, and the server reads which address is moving from the row the
 * token identifies.
 *
 * THERE IS NO CONFIRM BUTTON. The act of following a link from one's own mailbox IS the
 * confirmation — a second "yes, really" button would ask a person to re-assert something they
 * already did, and would leave the change hanging if they closed the tab. The token is single-use
 * and short-lived, which is what makes that safe.
 *
 * THE TOKEN RIDES AS THE LAST PATH SEGMENT, never `?token=` — X8, the same carrier the invitation
 * and reset screens use, because query strings are the ones copied wholesale into analytics tags
 * and error reports.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { callOperation } from '../api/operations';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { useLocale } from '../i18n/LocaleProvider';
import { routes } from '../routes';

type Outcome =
  | { readonly kind: 'working' }
  | { readonly kind: 'confirmed'; readonly loginChanged: boolean }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function EmailChangeConfirmScreen({ token }: { readonly token: string | null }): JSX.Element {
  const { t } = useLocale();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });
  /*
   * ONCE. React mounts an effect twice under StrictMode in development, and this token is
   * SINGLE-USE: a second call would consume nothing and answer "this link is not usable", which
   * would show a person a failure for a change that had just succeeded.
   */
  const sent = useRef(false);

  useEffect(() => {
    if (token === null || token === '' || sent.current) return;
    sent.current = true;
    void (async () => {
      try {
        const result = await callOperation('auth.email_change.confirm', { body: { token } });
        setOutcome({ kind: 'confirmed', loginChanged: result.loginChanged });
      } catch (error) {
        setOutcome({ kind: 'failed', failure: classifyActionError(error) });
      }
    })();
  }, [token]);

  return (
    <main className="pre-session" dir="rtl">
      <h1>{t('emailChange.title')}</h1>

      {token === null || token === '' ? (
        /*
         * SOMEBODY REACHED THIS ADDRESS BY HAND. Said as what it is — there is no token in the
         * address — rather than as a failure of the change, which has not been attempted.
         */
        <StateBlock titleHe={t('emailChange.noTokenTitle')} bodyHe={t('emailChange.noTokenBody')} />
      ) : outcome.kind === 'working' ? (
        <p aria-live="polite">{t('emailChange.working')}</p>
      ) : outcome.kind === 'failed' ? (
        <ActionFailureNotice failure={outcome.failure} />
      ) : (
        <StateBlock
          titleHe={t('emailChange.doneTitle')}
          /*
            WHICH IT WAS, because the two are different facts to the person standing there: one of
            them has to sign in with something new from now on.
          */
          bodyHe={
            outcome.loginChanged ? t('emailChange.doneLogin') : t('emailChange.doneContact')
          }
          nextStep={
            <p>
              <a href={routes.signIn}>{t('emailChange.goSignIn')}</a>
            </p>
          }
        />
      )}
    </main>
  );
}
